import { api_login_unico_url } from "./Urls";
import meuMaxLogo from "../Assets/CidadeDigital/meuMax.png";
import trindadeLogo from "../Assets/CidadeDigital/TrindadeNaPalmaDaMao.png";
import tresPalmeirasLogo from "../Assets/CidadeDigital/TRESPADIGITAL.png";

/**
 * Resolve o TÍTULO e a LOGO de um sistema para exibição, aplicando a regra
 * especial do "Cidade Digital" (rebrand por município conforme o banco).
 * Usado no card principal, na sidebar e no menu da conta — mantém tudo consistente.
 */
export function resolverSistemaDisplay(
  sistema: { titulo: string; logo: string },
  banco?: string
): { titulo: string; logoSrc: string } {
  let titulo = sistema.titulo;
  // Sem logo cadastrada, devolve string vazia em vez de montar uma URL com
  // "undefined" no fim — assim o <Avatar> já cai direto no fallback (iniciais)
  // sem disparar uma requisição que só pode dar 404.
  let logoSrc = sistema.logo
    ? `${api_login_unico_url}/foto-de-sistema/${sistema.logo}`
    : "";

  if (sistema.titulo === "Cidade Digital") {
    const b = banco?.toUpperCase?.();

    if (b === "2-B_MAX") {
      titulo = "meuMax";
      logoSrc = meuMaxLogo;
    } else if (b === "2-B_TRINDADE") {
      titulo = "Trindade na Palma da Mão";
      logoSrc = trindadeLogo;
    } else if (b === "1-B_TRESPALM") {
      titulo = "TRESPADIGITAL";
      logoSrc = tresPalmeirasLogo;
    } else if (b === "1-B_SJOSE") {
      titulo = "Protocolo Eletrônico";
    }
  }

  return { titulo, logoSrc };
}

/**
 * Monta o href de um sistema.
 *
 * Devolve só o PATHNAME de propósito: em produção o Login Único e os sistemas
 * moram no mesmo domínio (solucoes.sinsoft.com.br), então "/protocolo" navega
 * sem sair do host.
 *
 * ⚠️ O `catch` existe porque há sistema cadastrado com a URL SEM protocolo
 * (ex.: "solucoes.sinsoft.com.br/solicitacao-processo-licitatorio"). Nesse caso
 * `new URL()` lança, e devolver a string crua faz o href virar um caminho
 * RELATIVO — o link abriria ".../login-unico/solucoes.sinsoft.com.br/...", ou
 * seja, quebrado. Por isso tentamos de novo assumindo https.
 */
export function hrefDoSistema(url: string): string {
  const bruta = String(url ?? "").trim();
  if (!bruta) return "#";

  try {
    return new URL(bruta).pathname;
  } catch {
    try {
      return new URL(`https://${bruta}`).pathname;
    } catch {
      return bruta;
    }
  }
}

/**
 * URL ABSOLUTA do sistema — usada pelo card da home, que navega para o endereço
 * completo (e não só para o pathname, como a sidebar e o menu da conta).
 *
 * ⚠️ Mesmo motivo do `hrefDoSistema`: há sistema cadastrado sem "https://" na
 * URL. Sem o prefixo, o href vira um caminho RELATIVO e o card leva para
 * ".../login-unico/solucoes.sinsoft.com.br/..." em vez do sistema.
 */
export function urlAbsolutaDoSistema(url: string): string {
  const bruta = String(url ?? "").trim();
  if (!bruta) return "#";

  return /^https?:\/\//i.test(bruta) ? bruta : `https://${bruta}`;
}

/** Palavras que não ajudam a identificar o sistema nas iniciais. */
const PALAVRAS_IGNORADAS = ["DE", "DO", "DA", "DOS", "DAS", "E", "EM", "A", "O"];

/**
 * Duplas de iniciais que NÃO podem aparecer.
 *
 * Real: "Cadastro Único" -> C + Ú = "CÚ". Quando cair aqui, usamos as 3
 * primeiras letras da primeira palavra ("CAD"). Comparação sem acento, para
 * "CÚ" ser pego pela entrada "CU".
 */
const INICIAIS_EVITADAS = ["CU", "KK", "XX", "PP"];

function semAcento(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * Iniciais do sistema, para quando não há logo cadastrada.
 * "Cobrança de Água Municipal" -> "CA" | "ISSQN" -> "IS"
 *
 * Melhor que um ícone genérico: sem isso, todo sistema sem logo fica com o MESMO
 * quadradinho e o usuário não distingue um do outro na grade.
 */
export function iniciaisSistema(titulo: string): string {
  const palavras = String(titulo ?? "")
    .trim()
    .split(/\s+/)
    .filter((p) => p && !PALAVRAS_IGNORADAS.includes(p.toUpperCase()));

  if (palavras.length === 0) return "?";
  if (palavras.length === 1) return palavras[0].slice(0, 2).toUpperCase();

  const duas = (palavras[0][0] + palavras[1][0]).toUpperCase();

  // dupla infeliz -> usa as 3 primeiras letras da primeira palavra
  if (INICIAIS_EVITADAS.includes(semAcento(duas))) {
    return palavras[0].slice(0, 3).toUpperCase();
  }

  return duas;
}

/**
 * Matiz (0-360) derivada do título — o mesmo sistema sempre recebe a mesma cor,
 * e sistemas diferentes tendem a receber cores diferentes. Determinístico: não
 * depende de ordem, index nem de nada que mude entre renders.
 */
export function hueSistema(titulo: string): number {
  const texto = String(titulo ?? "");
  let hash = 0;

  for (let i = 0; i < texto.length; i++) {
    hash = (hash * 31 + texto.charCodeAt(i)) % 360;
  }

  return hash;
}
